import config from '../../config/config.js';
import {CLIENT_ERRORS, ErrorHandler} from 'starzplayer';

export class SessionStorageConfiguration {

    constructor(contentId, playKeyEncoded, trailer, sessionId, packageName, concurrency, isLive, packageParent, subscriptionIncludes, enableConcurrency) {
        this.contentId = contentId;
        this.playKeyEncoded = playKeyEncoded;
        this.trailer = !!trailer;
        this.sessionId = sessionId;
        this.packageName = packageName;
        this.concurrency = concurrency;
        this.isLive = isLive;
        this.packageParent = packageParent;
        this.subscriptionIncludes = subscriptionIncludes;
        this.enableConcurrency = enableConcurrency;
        return this;
    }

    /**
     * Get settings from the session storage
     * @return {SessionStorageConfiguration|boolean} false when key/settings object is not found, or the configuration.
     */
    static byStoredConfiguration() {
        const sessionObject = ConfigurationBuilder._asJson(ConfigurationBuilder._base64Decode(window.sessionStorage.getItem(config.sessionStorageKey)));
        return sessionObject && new SessionStorageConfiguration(sessionObject.contentId, sessionObject.playKeyEncoded, sessionObject.trailer, sessionObject.sessionId, sessionObject.packageName, sessionObject.concurrency, sessionObject.isLive, sessionObject.packageParent, sessionObject.subscriptionIncludes, sessionObject.enableConcurrency);
    }

    store() {
        window.sessionStorage && window.sessionStorage.setItem(config.sessionStorageKey, btoa(JSON.stringify({
            playKeyEncoded: this.playKeyEncoded,
            contentId: this.contentId,
            trailer: this.trailer,
            parentalControl: this.parentalControl,
            lbCDN: this.lbCDN,
            start: this.start,
            end: this.end,
            sessionId: this.sessionId,
            packageName: this.packageName,
            concurrency: this.concurrency,
            isLive: this.isLive,
            packageParent: this.packageParent,
            subscriptionIncludes: this.subscriptionIncludes,
            enableConcurrency: this.enableConcurrency
        })));
    }
}

export class ConfigurationBuilder {

    constructor(href) {
        this.playKeyDecoded = null;
        this.isTrailer = false;
        this.configuration = {};
        this.setUriVars(href);
        this.storedConfiguration = SessionStorageConfiguration.byStoredConfiguration() || new SessionStorageConfiguration();

        ErrorHandler.addOptions({
            language: this.language
        });

        return this;
    }

    /**
     *
     * Builds and validates the configuration. Implements builder pattern
     * @return ConfigurationBuilder
     */
    build() {
        if(!this.playKeyEncoded) {
            this.playKeyEncoded = this.storedConfiguration.playKeyEncoded;
            if(!this.playKeyEncoded) {
                ErrorHandler.throwError(CLIENT_ERRORS.INVALID_PLAYKEY, {
                    reason: 'No playkey specified, and no playkey available in the local storage'
                });
            }
            this.isTrailer = this.storedConfiguration.trailer;
        }

        if(!this.sessionId) {
            this.sessionId = this.storedConfiguration.sessionId;
        }
        if(!this.packageName) {
            this.packageName = this.storedConfiguration.packageName;
        }
        if(!this.concurrency) {
            this.concurrency = this.storedConfiguration.concurrency;
        }
        if(!this.isLive) {
            this.isLive = this.storedConfiguration.isLive;
        }
        if(!this.packageParent) {
            this.packageParent = this.storedConfiguration.packageParent;
        }
        if(!this.subscriptionIncludes) {
            this.subscriptionIncludes = this.storedConfiguration.subscriptionIncludes;
        }
        if(!this.enableConcurrency) {
            this.enableConcurrency = this.storedConfiguration.enableConcurrency;
        }

        this.playKeyDecoded = ConfigurationBuilder._base64Decode(this.playKeyEncoded);
        if(!this.playKeyDecoded) {
            ErrorHandler.throwError(CLIENT_ERRORS.INVALID_PLAYKEY, {
                reason: `Could not base64Decode the key. Key: ${this.playKeyEncoded}`
            });
        }

        const asJson = ConfigurationBuilder._asJson(this.playKeyDecoded);
        const csvKey = this.playKeyDecoded.split(',');
        if(asJson) {
            this.configuration = asJson;
        }
        else if(csvKey.length === 2) {
            csvKey.push(this.parentalControl);
            this.configuration = this._configByCSV.apply(this, csvKey);
        }
        else {
            ErrorHandler.throwError(CLIENT_ERRORS.INVALID_PLAYKEY, {
                reason: `The playkey is invalid. Must be either JSON or CSV. Given: ${this.playKeyDecoded}`
            });
        }

        ErrorHandler.addOptions({
            clientConfiguration: this.configuration
        });
        this._validate();
        this.storedConfiguration.playKeyEncoded = this.playKeyEncoded;
        this.storedConfiguration.contentId = this.contentId;
        this.storedConfiguration.trailer = this.isTrailer;
        this.storedConfiguration.parentalControl = this.parentalControl;
        this.storedConfiguration.lbCDN = this.lbCDN;
        this.storedConfiguration.start = this.start;
        this.storedConfiguration.end = this.end;
        this.storedConfiguration.sessionId = this.sessionId;
        this.storedConfiguration.packageParent = this.packageParent;
        this.storedConfiguration.subscriptionIncludes = this.subscriptionIncludes;
        this.storedConfiguration.isLive = this.isLive;
        this.storedConfiguration.concurrency = this.concurrency;
        this.storedConfiguration.packageName = this.packageName;
        this.storedConfiguration.enableConcurrency = this.enableConcurrency;
        this.storedConfiguration.store();

        return this;
    }

    getConfiguration() {
        return this.configuration;
    }

    static _asJson(string) {
        try {
            return JSON.parse(string);
        } catch(e) {
            console.debug(e);
            return false;
        }
    }

    static _base64Decode(base64string) {
        try {
            return atob(base64string);
        } catch(e) {
            console.debug(e);
            return false;
        }
    };

    _validate() {
        // Todo: add validations for the config
        return true;
    }

    setUriVars(url) {
        const error = err => ErrorHandler.throwError(CLIENT_ERRORS.INVALID_URI, err);
        const splitUri = url.split('?');
        // Parse parameters
        const uriParams = splitUri[1] && splitUri[1].split('&').reduce((ob, cur) => {
                const curSplit = cur.split('=');
                ob[curSplit[0]] = curSplit[1];
                return ob;
            }, {}) || {};
        this.playKeyEncoded = uriParams.play;
        this.isTrailer = 'trailer' in uriParams;
        this.recEngineModuleId = uriParams.recEngineModuleId;
        this.parentalControl = uriParams.parentalRating;
        this.lbCDN = uriParams.lbCDN;
        if(uriParams.enableConcurrency){
            this.enableConcurrency = uriParams.enableConcurrency;
            this.subscriptionIncludes = "false";
            this.packageParent = "false";
            this.concurrency = "false";
        }
        if(uriParams.start){
            this.start = uriParams.start;
            this.end = uriParams.end;
        }
        if(uriParams.packageName){
            this.packageName = uriParams.packageName;
        }
        if(uriParams.concurrency){
            this.concurrency = uriParams.concurrency;
        }
        if(uriParams.isLive){
            this.isLive = uriParams.isLive;
        }
        if(uriParams.packageParent){
            this.packageParent = uriParams.packageParent;
        }
        if(uriParams.subscriptionIncludes){
            this.subscriptionIncludes = uriParams.subscriptionIncludes;
        }
        if(uriParams.sessionId){
            this.sessionId = uriParams.sessionId;
        }

        // Parse & validate length of path
        const pathSplit = splitUri[0].split('/');

        if(pathSplit) {
            const language = pathSplit[3];
            const videoType = pathSplit[4];
            let urlFragments = pathSplit.length;
            let title = undefined;
            let contentId;
            let seriesInfo;

            if(videoType === 'series'){
                if(isNaN(pathSplit[pathSplit.length-1])){
                    title =  !/series/.exec(pathSplit[pathSplit.length-3]) ? pathSplit[pathSplit.length-3] : undefined;
                    contentId = pathSplit[pathSplit.length-2];
                    seriesInfo = pathSplit[pathSplit.length-1];
                } else {
                    title =  !/series/.exec(pathSplit[pathSplit.length-2]) ? pathSplit[pathSplit.length-2] : undefined;
                    contentId = pathSplit[pathSplit.length-1];
                }
            } else if(videoType === 'movies') {
                title =  !/movies/.exec(pathSplit[pathSplit.length-2]) ? pathSplit[pathSplit.length-2] : undefined;
                contentId = pathSplit[pathSplit.length-1];
            }

            // Validate input
            config.validLanguages.indexOf(language) === -1 && error({reason: 'Invalid language', given: language});
            config.validContentTypes.indexOf(videoType) === -1 && error({
                reason: 'Invalid content type',
                given: videoType
            });
            !contentId || isNaN(contentId) && error({reason: 'ContentId is not numeric', given: contentId});

            // Validate and set Series info
            if(videoType === 'series' && !this.isTrailer && seriesInfo) {
                if(seriesInfo === 'latest') {
                    this.latest = true;
                } else {
                    const episodeRegex = /s(\d*)e(\d*$)/g.exec(seriesInfo);
                    if(episodeRegex.length < 3) error({reason: 'Invalid episode information.', given: seriesInfo});
                    const season = parseInt(episodeRegex[1]);
                    const episode = parseInt(episodeRegex[2]);
                    if(!(season && episode)) error({reason: 'Invalid episode information.', given: seriesInfo});
                    this.season = season;
                    this.episode = episode;
                }
            }

            // Assign and return
            this.language = language;
            this.videoType = videoType;
            this.contentId = contentId;

        } else {
            ErrorHandler.throwError(CLIENT_ERRORS.INVALID_URI, {
                reason: 'Cannot split URI'
            });
        }
    }

    _configByCSV(userId, sessionToken, parentalControl) {

        let configuration = {
            'feed': `${config.apiUri}mediaCatalog/titles/${this.videoType}/${this.contentId}/?lang=${this.language}`,
            'sessionToken': sessionToken,
            'videoType': this.isTrailer ? 'trailer' : this.videoType,
            'css': '/empty.css',
            'language': this.language,
            'season': this.season,
            'episode': this.episode,
            'recEngineModuleId': this.recEngineModuleId,
        };

        if(userId && parentalControl) {
            let catchup =  this.start ? `&start=${this.start}&end=${this.end}` : '';
            configuration.feed += `&userId=${userId}&parentalControl=${parentalControl}${catchup}`;
        }

        if(this.recEngineModuleId) {
            configuration.recEngineModuleId = this.recEngineModuleId
        }

        if(this.parentalControl) {
            configuration.parentalControl = this.parentalControl;
        }

        if(this.lbCDN) {
            configuration.lbCDN = this.lbCDN;
        }

        if(this.start) {
            configuration.start = this.start;
        }

        if(this.end) {
            configuration.end = this.end;
        }
        if(this.packageName) {
            configuration.packageName = this.packageName;
        }
        if(this.concurrency) {
            configuration.concurrency = this.concurrency;
        }
        if(this.isLive) {
            configuration.isLive = this.isLive;
        }
        if(this.packageParent) {
            configuration.packageParent = this.packageParent;
        }
        if(this.subscriptionIncludes) {
            configuration.subscriptionIncludes = this.subscriptionIncludes;
        }
        if(this.sessionId) {
            configuration.sessionId = this.sessionId;
        }
        if(this.enableConcurrency) {
            configuration.enableConcurrency = this.enableConcurrency;
        }
        return JSON.parse(JSON.stringify(configuration)); // Trick to remove unused values
    }
}



// WEBPACK FOOTER //
// ./src/assets/js/configuration-builder.js